import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import './index.scss'
import CloseIcon from '../../../../asset/Close-X_Thin_F2F2F2_SVG.svg'
// Aliased imports: a mapDispatchToProps key sharing a name with its action creator makes
// Babel emit a named function expression that calls itself instead of the import, and
// recurses until the stack overflows (WEB-7031). Aliasing makes that impossible; the
// build is checked by scripts/check-dispatch-recursion.js.
import {
  appMessageClick as appMessageClickAction,
  clearAppMessage as clearAppMessageAction,
} from '../../../action/app-message-actions'
import { resolveTarget } from '../../../lib/app-message-link'

// iOS falls back to this when a message has no buttonText (AppMessageViewController).
const DEFAULT_BUTTON_TEXT = 'Learn More'

const AppMessage = ({ appMessage, appMessageClick, clearAppMessage }) => {
  const navigate = useNavigate()
  const [imageFailed, setImageFailed] = useState(false)

  // Closing records nothing. There is no dismiss endpoint on any platform — the mobile
  // close buttons also just close the dialog — which is why the admin Stats tab derives
  // "dismissed" as views minus clicks rather than measuring it (API-3585).
  const close = useCallback(() => clearAppMessage(), [clearAppMessage])

  useEffect(() => {
    if (!appMessage) return undefined
    const onKeyDown = event => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [appMessage, close])

  // A new message means a new image; without this a previously broken one stays hidden.
  useEffect(() => setImageFailed(false), [appMessage && appMessage.id])

  if (!appMessage) return null

  const { id, imageUrl, buttonText, buttonLink, deepLink } = appMessage

  const onAction = () => {
    // Not awaited, matching iOS and Android: the click is in flight and the user moves on
    // whether or not it lands.
    appMessageClick(id)

    const target = resolveTarget(buttonLink, deepLink, window.location.origin)
    if (target.kind === 'internal') navigate(target.to)
    else if (target.kind === 'external') window.open(target.href, '_blank', 'noopener,noreferrer')

    close()
  }

  // A portal to the end of <body>: the left panel, top menu and map type bar all sit at the
  // maximum z-index, so the overlay can only paint over them by coming later in the
  // document at the same value (WEB-7165).
  return ReactDOM.createPortal(
    <div
      className="app-message-overlay"
      onClick={close}
      role="presentation"
    >
      <div
        className="app-message-dialog"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="BaseMap message"
      >
        <button className="app-message-close" onClick={close} aria-label="Close">
          <img src={CloseIcon} alt="" />
        </button>

        {imageUrl && !imageFailed && (
          <img
            className="app-message-image"
            src={imageUrl}
            alt=""
            onError={() => setImageFailed(true)}
          />
        )}

        <button className="app-message-action" onClick={onAction}>
          {buttonText || DEFAULT_BUTTON_TEXT}
        </button>
      </div>
    </div>,
    document.body
  )
}

const mapStateToProps = state => ({
  appMessage: state.appMessage,
})

const mapDispatchToProps = dispatch => ({
  appMessageClick: id => dispatch(appMessageClickAction(id)),
  clearAppMessage: () => dispatch(clearAppMessageAction()),
})

export default connect(mapStateToProps, mapDispatchToProps)(AppMessage)
